import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, Sparkles, CheckCircle2, ArrowRight, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GradientButton } from "@/screens/_shared";
import { BOOST_GOALS, BOOST_PLANS, createBoost, extendBoost, getBoost, isBoostActive } from "@/lib/monetizationStore";
import { listEventsByOwner, listServicesByOwner } from "@/lib/entitiesStore";
import { initPaystackPayment, isPaystackConfigured } from "@/lib/paystack";

const SERVICE_BENEFITS = [
    "Increased visibility in search",
    "Priority listing across discover rails",
    "Higher booking exposure to your audience",
];

const EVENT_BENEFITS = [
    "Featured placement on home & discover",
    "Increased attendance reach",
    "Priority discovery for nearby users",
];

const formatRemaining = (endAt) => {
    const ms = Math.max(0, endAt - Date.now());
    const h = Math.floor(ms / 3_600_000);
    if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h left`;
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return `${h}h ${m}m left`;
};

/**
 * Boost flow: pick entity (service/event) -> goal -> plan -> pay -> success.
 * Only Premium users can boost.
 */

const Boost = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isPremium = !!user?.premium;

    const myServices = useMemo(() => (user ? listServicesByOwner(user.id) : []), [user]);
    const myEvents   = useMemo(() => (user ? listEventsByOwner(user.id) : []), [user]);

    const [entity, setEntity] = useState(null); // { kind, id, title }
    const [goal, setGoal] = useState("views");
    const [plan, setPlan] = useState("24h");
    const [step, setStep] = useState("pick"); // pick -> active|goal -> plan -> success
    const [busy, setBusy] = useState(false);
    const [reference, setReference] = useState("");
    const [extending, setExtending] = useState(false);
    const [paymentError, setPaymentError] = useState(null);

    if (!isPremium) return <LockedForFree onUpgrade={() => navigate("/premium")} />;

    const hasListings = myServices.length + myEvents.length > 0;
    const activeBoost = entity ? getBoost(user.id, entity.kind, entity.id) : null;
    const isActive = activeBoost && isBoostActive(activeBoost);

    const handlePickEntity = (picked) => {
        setEntity(picked);
        const b = getBoost(user.id, picked.kind, picked.id);
        if (b && isBoostActive(b)) {
            setStep("active");
        } else {
            setStep("goal");
        }
    };

    const confirmBoost = async () => {
        if (!entity) return;
        setPaymentError(null);
        if (!isPaystackConfigured()) {
            setPaymentError("Paystack is not configured yet.");
            return;
        }
        if (!user?.email) {
            setPaymentError("Add an email to your profile first — Paystack needs it.");
            return;
        }
        const amount = BOOST_PLANS.find((p) => p.id === plan)?.price;
        if (!amount) return;
        setBusy(true);
        const ref = `NEA-BST-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
        const res = await initPaystackPayment({
            email: user.email,
            amountNgn: amount,
            reference: ref,
            metadata: {
                purpose: "boost",
                entity: entity.kind,
                entityId: entity.id,
                plan,
                userId: user.id,
            },
            onSuccess: (paidRef) => {
                const r = extending
                    ? extendBoost(user.id, entity.kind, entity.id, plan)
                    : createBoost(user.id, entity.kind, entity.id, plan, goal);
                setBusy(false);
                if (r.ok) {
                    setReference(paidRef || r.reference);
                    setStep("success");
                    setExtending(false);
                }
            },
            onClose: () => setBusy(false),
        });
        if (!res.ok) {
            setBusy(false);
            setPaymentError("Could not start payment. Please try again.");
        }
    };

    return (
        <div className="screen" data-testid="boost-screen">
            <PageHeader title="Boost / Promote" />
            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5">
                {step === "pick" && (
                    <PickEntity
                        services={myServices}
                        events={myEvents}
                        hasListings={hasListings}
                        onPick={handlePickEntity}
                        onCreate={() => navigate("/create/service")}
                    />
                )}
                {step === "active" && entity && activeBoost && (
                    <BoostActive
                        entity={entity}
                        boost={activeBoost}
                        isActive={isActive}
                        onExtend={() => { setExtending(true); setStep("plan"); }}
                        onChange={() => { setEntity(null); setStep("pick"); }}
                    />
                )}
                {step === "goal" && (
                    <ChoiceGoal
                        kind={entity?.kind}
                        value={goal}
                        onChange={setGoal}
                        onNext={() => setStep("plan")}
                    />
                )}
                {step === "plan" && (
                    <ChoosePlan
                        value={plan}
                        onChange={setPlan}
                        onNext={confirmBoost}
                        busy={busy}
                    />
                )}
                {step === "success" && (
                    <Success
                        reference={reference}
                        onDone={() => navigate("/profile")}
                    />
                )}
            </div>
        </div>
    );
};

const LockedForFree = ({ onUpgrade }) => (
    <div className="screen" data-testid="boost-locked-screen">
        <PageHeader title="Boost / Promote" />
        <div className="flex-1 px-6 py-8 text-center">
            <span
                className="mx-auto h-16 w-16 rounded-full flex items-center justify-center text-primary-foreground"
                style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))" }}
            >
                <Sparkles size={26} />
            </span>
            <h2 className="mt-4 font-display text-[20px] font-semibold">Boost is Premium-only</h2>
            <p className="mt-1.5 text-[13px] text-muted-foreground max-w-xs mx-auto">
                Upgrade to boost your listings and reach more people.
            </p>
            <div className="mt-6 max-w-xs mx-auto">
                <GradientButton testId="boost-upgrade-btn" onClick={onUpgrade}>
                    <span className="inline-flex items-center gap-2">
                        <Rocket size={16} /> Upgrade to Premium
                    </span>
                </GradientButton>
            </div>
        </div>
    </div>
);

const PickEntity = ({ services, events, hasListings, onPick, onCreate }) => (
    <div data-testid="boost-pick">
        <h2 className="font-display text-[20px] font-semibold mb-3">Choose what to boost</h2>
        {!hasListings ? (
            <div className="rounded-2xl border border-border bg-card p-5 text-center">
                <p className="text-[13.5px] text-muted-foreground">
                    You don't have any listings yet.
                </p>
                <div className="mt-4">
                    <GradientButton testId="boost-create-first" onClick={onCreate}>
                        Create your first service
                    </GradientButton>
                </div>
            </div>
        ) : (
            <div className="space-y-4">
                {services.length > 0 && (
                    <SectionBlock title="Services">
                        {services.map((s) => (
                            <BoostItem
                                key={s.id}
                                testId={`boost-item-service-${s.id}`}
                                image={s.image}
                                title={s.name}
                                subtitle={s.category}
                                onPick={() => onPick({ kind: "service", id: s.id, title: s.name })}
                            />
                        ))}
                    </SectionBlock>
                )}
                {events.length > 0 && (
                    <SectionBlock title="Events">
                        {events.map((e) => (
                            <BoostItem
                                key={e.id}
                                testId={`boost-item-event-${e.id}`}
                                image={e.image}
                                title={e.title}
                                subtitle={e.category}
                                onPick={() => onPick({ kind: "event", id: e.id, title: e.title })}
                            />
                        ))}
                    </SectionBlock>
                )}
            </div>
        )}
    </div>
);

const SectionBlock = ({ title, children }) => (
    <div>
        <h3 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2 ml-1">
            {title}
        </h3>
        <div className="space-y-2">{children}</div>
    </div>
);

const BoostItem = ({ image, title, subtitle, onPick, testId }) => (
    <button
        data-testid={testId}
        onClick={onPick}
        className="w-full flex items-center gap-3 p-2.5 rounded-2xl border border-border bg-card shadow-soft text-left active:scale-[0.99] transition-transform"
    >
        <span className="h-12 w-12 rounded-xl overflow-hidden bg-muted shrink-0">
            {image && <img src={image} alt="" className="h-full w-full object-cover" />}
        </span>
        <span className="flex-1 min-w-0">
            <span className="block font-display text-[13.5px] font-semibold truncate">{title}</span>
            <span className="block text-[11.5px] text-muted-foreground">{subtitle}</span>
        </span>
        <ArrowRight size={16} className="text-muted-foreground" />
    </button>
);

const ChoiceGoal = ({ kind, value, onChange, onNext }) => {
    const benefits = kind === "event" ? EVENT_BENEFITS : SERVICE_BENEFITS;
    return (
        <div data-testid="boost-goal">
            <h2 className="font-display text-[20px] font-semibold mb-1">What's your goal?</h2>
            <p className="text-[12.5px] text-muted-foreground mb-3">
                Boosting your {kind === "event" ? "event" : "service"} unlocks:
            </p>
            <ul className="mb-4 space-y-1.5">
                {benefits.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-[12.5px]">
                        <CheckCircle2 size={13} className="text-primary shrink-0" />
                        {b}
                    </li>
                ))}
            </ul>
            <div className="space-y-2">
                {BOOST_GOALS.map((g) => (
                    <button
                        key={g.id}
                        data-testid={`boost-goal-${g.id}`}
                        onClick={() => onChange(g.id)}
                        className={[
                            "w-full flex items-center gap-3 p-3.5 rounded-2xl border transition-[transform,border-color] active:scale-[0.99]",
                            value === g.id
                                ? "border-primary bg-primary/5 shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
                                : "border-border bg-card",
                        ].join(" ")}
                    >
                        <span className="flex-1 text-left text-[14px] font-medium">{g.label}</span>
                        {value === g.id && <CheckCircle2 size={18} className="text-primary" />}
                    </button>
                ))}
            </div>
            <div className="mt-6">
                <GradientButton testId="boost-goal-next" onClick={onNext}>Continue</GradientButton>
            </div>
        </div>
    );
};

const BoostActive = ({ entity, boost, isActive, onExtend, onChange }) => (
    <div data-testid="boost-active">
        <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5">
            <div className="flex items-center gap-2 mb-2">
                <span className={[
                    "h-7 px-2.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wide inline-flex items-center gap-1",
                    isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
                ].join(" ")}>
                    <Sparkles size={11} />
                    {isActive ? "Boost Active" : "Boost Expired"}
                </span>
            </div>
            <h2 className="font-display text-[18px] font-semibold">{entity.title}</h2>
            <p className="text-[12px] text-muted-foreground mt-1 inline-flex items-center gap-1">
                <Clock size={11} />
                {isActive ? formatRemaining(boost.endAt) : "Boost has ended"}
            </p>
            <div className="mt-4 grid grid-cols-3 gap-2">
                <Stat label="Views"    value={boost.views || 0} />
                <Stat label="Clicks"   value={boost.clicks || 0} />
                <Stat label="Messages" value={boost.messages || 0} />
            </div>
        </div>
        <div className="mt-4 space-y-2">
            <GradientButton testId="boost-extend-btn" onClick={onExtend}>
                {isActive ? "Extend Boost" : "Re-activate Boost"}
            </GradientButton>
            <button
                data-testid="boost-change-entity"
                onClick={onChange}
                className="w-full h-12 rounded-2xl bg-secondary text-secondary-foreground font-medium text-[13.5px]"
            >
                Boost something else
            </button>
        </div>
    </div>
);

const Stat = ({ label, value }) => (
    <div className="rounded-xl bg-card border border-border p-2.5 text-center">
        <div className="font-display text-[16px] font-semibold">{value}</div>
        <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
);

const ChoosePlan = ({ value, onChange, onNext, busy, paystackConfigured = true, paymentError = null }) => {
    const selected = BOOST_PLANS.find((p) => p.id === value);
    return (
        <div data-testid="boost-plan">
            <h2 className="font-display text-[20px] font-semibold mb-3">Pick a duration</h2>
            <div className="space-y-2">
                {BOOST_PLANS.map((p) => (
                    <button
                        key={p.id}
                        data-testid={`boost-plan-${p.id}`}
                        onClick={() => onChange(p.id)}
                        className={[
                            "w-full flex items-center gap-3 p-3.5 rounded-2xl border transition-[transform,border-color] active:scale-[0.99]",
                            value === p.id
                                ? "border-primary bg-primary/5 shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
                                : "border-border bg-card",
                        ].join(" ")}
                    >
                        <span className="flex-1 text-left">
                            <span className="block font-display text-[14px] font-semibold">{p.label}</span>
                            <span className="block text-[11.5px] text-muted-foreground">Featured placement · wider reach</span>
                        </span>
                        <span className="font-display font-semibold">₦{p.price.toLocaleString()}</span>
                    </button>
                ))}
            </div>
            <div className="mt-6">
                {!paystackConfigured && (
                    <div
                        data-testid="boost-paystack-warning"
                        className="mb-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[12px] font-medium px-3.5 py-2"
                    >
                        Paystack is not configured yet.
                    </div>
                )}
                {paymentError && (
                    <p
                        data-testid="boost-payment-error"
                        className="mb-2 text-[12px] text-destructive font-medium text-center"
                    >
                        {paymentError}
                    </p>
                )}
                <GradientButton testId="boost-plan-pay" onClick={onNext} disabled={busy}>
                    {busy ? "Processing…" : `Pay ₦${selected?.price.toLocaleString()} & boost`}
                </GradientButton>
                <p className="text-[11px] text-muted-foreground text-center mt-2">
                    Secure checkout powered by Paystack.
                </p>
            </div>
        </div>
    );
};

const Success = ({ reference, onDone }) => (
    <div className="text-center pt-6" data-testid="boost-success">
        <span
            className="mx-auto h-16 w-16 rounded-full flex items-center justify-center text-primary-foreground"
            style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))" }}
        >
            <Rocket size={26} />
        </span>
        <h2 className="mt-4 font-display text-[20px] font-semibold">Boost activated successfully</h2>
        <p className="mt-1 text-[12.5px] text-muted-foreground">
            Ref: <span className="font-mono">{reference}</span>
        </p>
        <div className="mt-6">
            <GradientButton testId="boost-success-done" onClick={onDone}>
                Done
            </GradientButton>
        </div>
    </div>
);

export default Boost;
